import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Service from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';
// Reuse the medication modal styling for a consistent look.
import './SuggestMedicationModal.css';

/**
 * Admin modal to suggest OK Capsule supplements to a patient.
 * Pricing is NOT set here — supplement pricing lives in Stripe (the subscription
 * plans). This modal only selects which pack(s) to suggest.
 */
const SuggestSupplementModal = ({ patient, onClose }) => {
  const [products, setProducts] = useState([]);
  // Product ids (strings) that are checked — seeded from the patient's existing suggestions.
  const [selectedIds, setSelectedIds] = useState([]);
  const [hasExisting, setHasExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchExistingSuggestions();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await Service.getSupplementProducts();
      setProducts(res.data || []);
    } catch (error) {
      showToast('error', 'Failed to fetch OK Capsule products');
    } finally {
      setLoading(false);
    }
  };

  // Pre-check whatever the patient was already suggested.
  const fetchExistingSuggestions = async () => {
    try {
      const res = await Service.getPatientSupplementSuggestions(patient.id);
      const suggestions = res.data?.suggestions || [];
      setHasExisting(suggestions.length > 0);
      setSelectedIds(suggestions.map((s) => String(s.product_id)));
    } catch (error) {
      // Non-fatal — the admin can still make a fresh selection.
      showToast('error', 'Failed to fetch existing supplement suggestions');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await Service.syncSupplementProducts();
      showToast('success', 'Catalog synced from OK Capsule');
      await fetchProducts();
    } catch (error) {
      showToast('error', error.response?.data?.detail || 'Failed to sync catalog');
    } finally {
      setSyncing(false);
    }
  };

  const toggleProduct = (product) => {
    const id = String(product.id);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const handleSuggest = async () => {
    if (selectedIds.length === 0) {
      showToast('error', 'Please select at least one supplement');
      return;
    }

    setSubmitting(true);
    try {
      await Service.suggestSupplements(patient.id, {
        products: selectedIds.map((id) => ({ product_id: id })),
      });
      showToast('success', 'Supplements suggested successfully');
      onClose();
    } catch (error) {
      showToast('error', error.response?.data?.detail || 'Failed to suggest supplements');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Suggest OK Capsule Supplements</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="patient-info">
            Patient:{' '}
            <strong>
              {patient.first_name} {patient.last_name}
            </strong>{' '}
            ({patient.email})
          </p>

          {hasExisting && <p className="patient-info">Already suggested items are pre-selected.</p>}

          <div style={{ marginBottom: '10px' }}>
            <button className="btn btn-primary" onClick={handleSync} disabled={syncing || loading}>
              {syncing ? 'Syncing…' : 'Refresh from OK Capsule'}
            </button>
          </div>

          {loading ? (
            <p>Loading products…</p>
          ) : products.length === 0 ? (
            <p>No OK Capsule products found. Click “Refresh from OK Capsule” to sync the catalog.</p>
          ) : (
            <div className="offerings-list">
              {products.map((product) => {
                const isSelected = selectedIds.includes(String(product.id));
                return (
                  <div
                    key={product.id}
                    className={`offering-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleProduct(product)}
                    style={{ cursor: 'pointer' }}
                  >
                    <input type="checkbox" checked={isSelected} onChange={() => toggleProduct(product)} />
                    <label style={{ flex: 1, cursor: 'pointer' }}>{product.title}</label>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-danger" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSuggest} disabled={submitting || selectedIds.length === 0}>
            {submitting ? 'Suggesting…' : 'Suggest'}
          </button>
        </div>
      </div>
    </div>
  );
};

SuggestSupplementModal.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SuggestSupplementModal;
